/**
 * 《사흘》 v7 묶음 — 배럴(`level.ts`)이 기대하는 심볼을 전부 채운다.
 *
 * **아직 껍데기 단계다.** 지형·방·벽·건물만 v7 것이고, 오브젝트(`PROPS`)와
 * 규칙은 비어 있다. 마을이 제대로 생겼는지 눈으로 보는 것이 이 단계의 전부다 —
 * 여기서 틀린 것을 안 잡으면 오브젝트 110개를 엉뚱한 방에 놓게 된다.
 *
 * 잠금·라운드·조는 v3 것을 그대로 빌려 쓴다. 타입만 맞으면 되고, v7 값은
 * 오브젝트를 놓은 뒤에 짠다.
 */
export {
  BUILDINGS,
  CLEARINGS,
  HILL_Y,
  OUTDOOR,
  ROADS,
  M_PER_MIN,
  RAMPS,
  ROOMS,
  roomAt,
  SAHOLV7_LEVEL,
  spawnPoint,
  WALL_H,
  WALL_T,
  WALLS,
} from './level-saholv7';

import { PLACED, PLACED_PROPS } from './level-saholv7.placed';
import type { BodyPartDef, ForensicDef, LockDef } from './level-defs';
export type { Archetype, BuildingDef, ForensicDef, FurnDef, HouseStyle, PropDef, PropKind, RoomDef, WallDef } from './level-defs';
export type { LampDef, OutdoorDef, RoadDef, TreeDef } from './outdoor';
export { CLOTH, DARKWOOD, SCHOOL_GREEN, SNOW, STEEL, WOOD } from './level-defs';
import { HILL_Y, ROOMS } from './level-saholv7';
import type { PieceDef } from './level-defs';
import type { FurnDef, RoundDef } from './level-defs';
export { EYE_H, PLAYER_R } from './level-defs';

import type { ObjState } from './protocol';
import { readyPairOf, roundGuards } from './rules';
import type { LinkDef } from './level-defs';
import type { PropDef } from './level-defs';

/** 창·문 개구부는 `wallRun` 이 뚫으면서 채운다 — 여기서 다시 적지 않는다 */
export { DOORWAYS, WINDOWS, WALL_RUNS, rebuildWalls, type OpeningOverride, type WallRunDef } from './level-saholv7';

export const WIN_Y = { sill: 0.95, head: 2.15, half: 0.85 };

/** 가구와 조각 — 실내 가구는 오브젝트 단계에서 채운다 */
export const FURNITURE: FurnDef[] = [];

/**
 * 바깥에 서 있는 것들 — **집만 있으면 마을이 아니다.**
 *
 * v7 바깥은 집 여섯 채가 눈밭에 서 있는 상태였다. 컷마다 「사람이 사는 곳」이
 * 아니라 「모형」으로 읽힌 이유는 조명도 지형도 아니고 **집 옆에 쌓여 있는 것이
 * 하나도 없어서**였다. 마을을 마을로 만드는 것은 전신주·장독대·장작더미·리어카다.
 *
 * 배치 규칙 셋:
 * 1. **카메라 시선 축을 비운다.** `docs/scenes/<id>/scene.json` 의 촬영 자리와
 *    시선 방향을 계산해 축에서 최소 1.5m 는 띄웠다. 축 위에 둔 나무 한 그루가
 *    대로 컷 하나를 통째로 날린 적이 있다 (`CLEARINGS` 주석 참고).
 * 2. **가로등과 전신주를 같은 줄에 세우지 않는다.** 가로등은 대로 남쪽(z=13),
 *    전신주는 북쪽(z=19.2)이다. 한 줄에 겹치면 둘 다 「기둥」으로 뭉개진다.
 * 3. **굴뚝 없는 집에는 장작더미를 두지 않는다.** 배정호 집은 `stack: 'none'`
 *    이라 땔감이 필요 없다 — 그 마당이 비어 있는 것이 「제일 외진 집」이다.
 *
 * y 는 전부 지면(0), 언덕 위 한서진 집만 `HILL_Y`. 눈이 20cm 지만 지면 자체는
 * y=0 이다 (가로등 기둥도 y=0 에서 선다).
 */
export const PIECES: PieceDef[] = [
  /* 관계 DSL 로 푼 방 (level-saholv7.place.ts → tools/place-solve.ts → level-saholv7.placed.ts) */
  ...PLACED,
  /* ── 전신주 — 대로 북쪽. 늘어선 간격이 대로의 길이를 말한다 ── */
  /* 목공동 난로 — 몸통만. 재받이는 PROPS 의 lid `shop_stove_tray` 가 밑에서 앞으로 빠진다 (2026-09-09) */
  /* ── 한서진 집 거실 채우기 (2026-09-10, 배치 에디터로 놓고 확정). 언덕 y 3.6.
   *    걸어 보니 소파·탁자·서랍장이 서쪽 구석에 몰리고 동쪽 절반이 맨 벽·빈 마루였다 ── */
  // **난로는 바깥벽에.** 북벽은 침실과의 칸막이라 연통이 침실로 나가는 꼴이었다(Chris 2026-09-10). 서벽 창(z=−56) 북쪽 옆,
  // 등(+Z)이 서벽(−X)을 보게 rot −π/2. 연통 팔꿈치가 뒤로 0.61 — 벽 안쪽 면(x=−7.85)에 닿게 x=−7.24
  { piece: 'coal_stove_bare', room: 'hs_room', pos: [-7.24, 3.6, -57.5], rot: -Math.PI / 2 },
  { piece: 'bookshelf', room: 'hs_room', pos: [0.2, 3.6, -57.66], rot: Math.PI },          // 북동 구석
  { piece: 'dining_table', room: 'hs_room', pos: [-2.2, 3.6, -55.0], rot: 0 },             // 동쪽 절반 가운데(동벽 문 z=−55 앞을 비운다)
  { piece: 'chair', room: 'hs_room', pos: [-3.0, 3.6, -55.0], rot: -Math.PI / 2 },
  { piece: 'chair', room: 'hs_room', pos: [-1.4, 3.6, -55.0], rot: Math.PI / 2 },
  { piece: 'potted_plant', room: 'hs_room', pos: [-1.6, 3.6, -57.5], rot: 0 },             // 침실 문 오른쪽(난로가 있던 자리)
  { piece: 'work_coat_rack', room: 'hs_room', pos: [0.65, 3.6, -53.2], rot: Math.PI / 2 }, // 동벽 남쪽 끝 — 현관 앞 구역(±1.1) 밖
  { piece: 'pedal_bin', room: 'hs_room', pos: [-7.6, 3.6, -52.5], rot: 0 },                // 남서 구석
  /* ── 한서진 집 침실 채우기 (2026-09-10, 배치 에디터). 남쪽·동쪽 절반이 빈 마루였다. 가구만 — 단서는 그대로 ── */
  // 휴지통은 place.ts 의 bed_bin(책상 옆 관계 배치) — 손 좌표로 두니 솔버의 책상 자리와 겹쳐 책상·의자·폰·충전기가 통째로 빠졌다(2026-09-10)
  /* ── 업무공간·다용도실 채우기 (2026-09-10, 배치 에디터). 문 앞 구역(현관·침실·업무 문)은 비운다 ── */
  { piece: 'steel_cabinet', room: 'hs_work', pos: [4.6, 3.6, -52.5], rot: 0 },     // 남벽
  { piece: 'parcel', room: 'hs_work', pos: [1.65, 3.6, -52.6], rot: 0.17 },       // 남서 구석 택배 둘
  { piece: 'parcel', room: 'hs_work', pos: [2.2, 3.6, -52.7], rot: -0.26 },
  // 다용도실 추가분은 place.ts(util_briq*·util_laundry·util_sangja·util_crate) — 손 좌표가 솔버의 선반·양동이와 겹쳐 셋이 빠졌다(2026-09-10)
  { piece: 'power_pole', room: 'outside', pos: [-40, 0, 19.2] },
  { piece: 'power_pole', room: 'outside', pos: [-18, 0, 19.2], rot: 0.12 },
  { piece: 'power_pole', room: 'outside', pos: [6, 0, 19.2], rot: -0.08 },
  { piece: 'power_pole', room: 'outside', pos: [34, 0, 19.2] },
  { piece: 'power_pole', room: 'outside', pos: [62, 0, 19.2], rot: 0.1 },
  // 배정호 외길 — 둘뿐이고 끝의 것은 집까지 못 간다. 「전기가 겨우 닿는 곳」
  { piece: 'power_pole', room: 'outside', pos: [79.5, 0, 10], rot: 1.57 },
  { piece: 'power_pole', room: 'outside', pos: [79.5, 0, 0], rot: 1.57 },
  { piece: 'power_pole', room: 'outside', pos: [83, 0, -5.2], rot: 1.4 },

  /* ── 장독대 — 집집마다 마당 한쪽. 뚜껑 위에 눈이 얹힌다 ── */
  { piece: 'jangdok_stand', room: 'outside', pos: [-46.5, 0, -12], rot: -1.57 },
  { piece: 'jangdok_stand', room: 'outside', pos: [45.5, 0, -12], rot: 1.57 },
  { piece: 'jangdok_stand', room: 'outside', pos: [-44.5, 0, 24], rot: 1.57 },
  { piece: 'jangdok_stand', room: 'outside', pos: [59.5, 0, 24], rot: -1.57 },
  // 언덕 위 한서진 집 — 마당이 넓다
  { piece: 'jangdok_stand', room: 'outside', pos: [8, HILL_Y, -63], rot: -1.57 },

  /* ── 장작더미 — 굴뚝 있는 집만 (송이경·명주원·윤가람·차유리·한서진) ── */
  { piece: 'wood_pile', room: 'outside', pos: [-42, 0, -5], rot: 0.06 },
  { piece: 'wood_pile', room: 'outside', pos: [42, 0, -5], rot: -0.05 },
  { piece: 'wood_pile', room: 'outside', pos: [-47, 0, 16.6], rot: 0.03 },
  { piece: 'wood_pile', room: 'outside', pos: [56, 0, 17], rot: -0.04 },
  { piece: 'wood_pile', room: 'outside', pos: [7, HILL_Y, -58], rot: -1.52 },

  /* ── 리어카 — 세워 둔 것 둘. 마을에 「일하는 사람」이 있다는 표시 ── */
  // 교문 동쪽. 히어로 컷(0,24 → -Z)의 오른쪽 앞을 채우되 문주(x ±3.5)는 비운다
  { piece: 'hand_cart', room: 'outside', pos: [5.2, 0, 12.8], rot: 0.35 },
  // 작업동 앞
  { piece: 'hand_cart', room: 'outside', pos: [-13.2, 0, 27], rot: -1.2 },

  /* ================================================================== *
   * 실내 — 별동 네 방.
   *
   * **v7 의 제일 큰 구멍이 여기였다.** 방 열일곱 개에 가구가 0 개였다.
   * 바깥은 전신주·장독대·장작더미까지 놓아 두고 안은 손도 안 댔으니, 문을
   * 열면 콘크리트 바닥과 형광등만 있는 빈 상자가 나왔다.
   *
   * 좌표를 찍기 전에 벽 데이터에서 뽑아 둔 것 — **눈으로 맞추면 반드시 문에
   * 가구를 세운다:**
   *   문 폭 1.9m (`DOOR_HALF` 0.95)  · 창턱 0.95m (`WIN_SILL`) · 벽 두께 0.3m
   *
   * 그래서 규칙 셋:
   * 1. **문 중심 ±1.2m 는 비운다.** 못 지나가는 문은 문이 아니라 벽이다.
   * 2. **창 앞은 허리 높이까지.** 0.95m 보다 높은 것을 창 앞에 세우면 밖에서
   *    본 창이 통째로 검게 막힌다 — 별동은 정면 창을 여섯 개나 낸 건물이다.
   * 3. **벽면은 경계선에서 0.15m 안쪽이다.** 거기에 물건 깊이의 절반을 더한
   *    값이 중심 좌표다. 경계선에 맞추면 절반이 벽에 박힌다.
   * ================================================================== */

  /* ── 목공·시설 작업실(명주원). 북문 x=-10 · 북창 -14,-6 · 남창 -12,-6 · 서창 z=36 ── */
  // **집진기가 이 방의 주인공이다.** 22:04 에 켜져 00:58 까지 돌았고 그동안
  // 사람은 없었다 — 알리바이가 기계에서 나온 방이다. 작업대 옆, 문으로
  // 들어서면 바로 보이는 자리에 둔다. 구석에 밀어 넣으면 단서가 숨는다
  // 공구 걸이판은 **남쪽 벽** — 북문으로 들어오면 정면이다. 빈 자리 하나가
  // 단서인 물건이라 안 보이는 벽에 걸면 없는 것과 같다. 남창(x=-12,-6)은 피한다
  // 「문 옆 서랍장」 — 앞판 아래 25cm 의 손톱 자국이 단서 본문에 숫자로 박혀
  // 있다. 문(x=-10) 바로 옆이어야 그 글이 말이 된다

  /* ── 도예 작업실(윤가람). 북문 x=2 · 북창 -1,5 · 남창 0,6 · 동문 z=34,41 ── */
  // 물레는 **북창 밑**이다. 창턱 0.95m 에 물레 높이 0.62m 라 창을 안 막고,
  // 앉으면 창으로 들어온 빛이 손 위에 떨어진다 — 도예 공방의 자리는 늘 창가다
  // 유약장. **문이 -Z 라 남벽에 붙이면 문이 방을 본다.** 자물쇠가 물린 채인
  // 물건이고 코드는 가마실 소성표에 있다 — 둘이 다른 방에 있어야 뒤지게 된다

  /* ── 가마실. 북 반입문 x=12 · 남문 x=12 · 서문 z=34 · 북창 10,14 · 동창 z=34 ── */
  // 문 둘이 x=12 로 **일직선**이다(장작·기물을 밀어 넣는 동선). 그 통로
  // 11.05~12.95 는 z 를 통째로 비운다
  // 소성표가 유약장 자물쇠 코드(1250)의 원본이다. 상자 위에 얹어 눈높이로 올린다

  /* ── 사진·기록실(송이경). 서문 z=41 · 북문 x=12 · 남창 x=12 · 동창 z=41 ── */
  // 인화 트레이는 받침 작업대와 천장 줄을 제 몸에 달고 있다 — 따로 책상을
  // 놓지 않는다. 접시 셋이 나란해야 현상·정지·정착의 순서가 읽힌다
  // 옛 개인전 포스터. **연도가 읽혀야 하는 물건**이라 창(x=12)에서 비켜 세우고
  // 눈높이 위(1.35~2.05m)에 건다

  /* ================================================================== *
   * 두 번째 바퀴 — **한 컷 찍고 나서 채운 것들.**
   *
   * 첫 배치를 찍어 보니 방이 「물건 몇 개 놓인 창고」였다. 별동 한 칸이
   * 12x14m, 168 평방미터다. 벽을 따라 열 몇 개를 세워 봐야 바닥 한복판이
   * 통째로 비어서 화면 아래 절반이 맨 콘크리트로 나온다.
   *
   * 그래서 **가운데를 채우고 벽선을 잇는다:**
   *  - 연탄난로를 방 한복판에 놓는다. 산골 별동에 난방이 없을 리 없고,
   *    무엇보다 **바닥 한가운데에 설 이유가 있는 유일한 물건**이다.
   *  - 벽에 붙는 것은 띄엄띄엄 두지 말고 **줄로 잇는다**(작업대 - 수납장 -
   *    선반). 하나씩 떨어져 있으면 벽이 아니라 벽에 놓인 점이 된다.
   *  - 벽 위쪽(1.8~2.1m)이 전부 비어 있었다. 게시판·달력을 걸어 **시선
   *    높이 위**를 채운다.
   *
   * 창턱 0.95m 규칙은 그대로다 — 여기 새로 놓는 것 중 창 앞에 서는 것은
   * 조리대(0.9)와 장작더미(0.92)뿐이고 둘 다 창턱 아래다.
   * ================================================================== */

  /* -- 목공.시설 작업실 -- */
  // 난로는 문축(x=-10)을 비껴 세운다. 연통이 시선을 가르면 방이 둘로 보인다
  // 걸이판 밑에 조리대를 깔아 「작업 벽」을 만든다. 공구판만 떠 있으면 벽이 빈다
  // 장작더미는 **처마 밑 물건이다** — 맨 윗줄에 눈을 이고 있게 만들어져 있어서
  // 실내에 놓으니 방 안에 눈이 쌓였다. 나무 상자로 바꾼다.
  // 게시판은 북벽 창 사이 빈 폭(x=-12)에 건다. 창은 -14 와 -6 이다

  /* -- 도예 작업실 -- */
  // 흙을 치대는 조리대. 남창(x=0,6) 사이에 끼워 넣는다
  // 흙 자루 — 도예실에서 바닥에 굴러다니는 게 당연한 유일한 물건이다

  /* -- 가마실 -- */
  // 가마 땔감. 북창(x=10,14) 턱이 0.95 라 0.92 짜리 장작더미는 창을 안 막는다

  /* -- 사진.기록실 -- */
  /* ================================================================== *
   * 세 번째 바퀴 — **선반이 비어 있었다.**
   *
   * 두 번째 컷을 열어 보니 방은 「창고」를 벗어났는데 두 가지가 남았다.
   *  1) **앵글 선반 일곱 대가 전부 빈 판때기였다.** 빈 선반은 물건이
   *     없다는 뜻이 아니라 **아직 안 만든 방**이라는 뜻으로 읽힌다.
   *     `shelf_rack` 은 h 1.9 · 4단이고 단 높이는 0.12/0.70/1.28/1.86 이다
   *     (`(1.9-0.16)/3` 간격). 판 두께 0.018 이라 **얹는 높이는 +0.009**.
   *     깊이가 0.45 뿐이라 궤짝(0.56)은 못 얹는다 — 상자·자루·서류만.
   *  2) 12x14m 방 두 개(목공·도예)는 **한가운데가 여전히 비었다.** 벽만
   *     채우면 화면 아래 절반이 맨 바닥이다. 작업대를 하나 더 놓아
   *     **가운데에 섬을 만든다** — 공방은 원래 가운데서 일한다.
   *
   * 마을 조각(`saholVillage.ts`)은 **전부 눈을 쓴다** — power_pole,
   * jangdok_stand, wood_pile, hand_cart 넷 다 `snowedSlots` 다. 실내에
   * 쓰면 방 안에 눈이 쌓인다(두 번째 바퀴에서 장작더미로 이미 겪었다).
   * 여기서는 하나도 안 쓴다.
   * ================================================================== */

  /* -- 목공.시설: 가운데 섬 -- */
  /* 연탄난로 옆 연탄 — 난로만 있고 연료가 없으면 소품이 아니라 장식이다 */
  /* -- 목공: 선반 두 대 채우기 (x=-15.6, rot -PI/2 -> 폭은 z 축) -- */

  /* -- 도예: 가운데 섬 -- */
  /* -- 도예: 선반 두 대 (x=7.63, rot PI/2) -- */

  /* -- 가마실: 선반 두 대 (x=15.63, rot PI/2) -- */

  /* -- 사진.기록실: 선반 한 대 (x=8.38, rot -PI/2) -- */

  /* ================================================================== *
   * 관사 — 임시수사실 · 신문실. 예능 루틴의 「탐정 방」.
   * 수사자료 보드(E04·E14 가 여기 도착한다)와 포렌식 테이블은 수사실에,
   * 신문실은 책상 하나에 의자 둘 — 마주 앉는 방이라 그 이상 두지 않는다.
   * ================================================================== */
  // **북벽 창을 피해 동쪽으로 민다.** 관사 북벽 창은 x=31(WIN_HALF 0.85 → 30.15~31.85,
  // 높이 0.95~2.15)인데 보드가 x 30.62~31.78 · y 0.82~1.68 로 창구멍을 정통으로 덮고
  // 있었다. z +2 이동 전 좌표를 그대로 옮겨서 생긴 것이다.
  // **복합기를 바닥에 두면 안 된다.** 2026-09-05 H2 컷에서 상자 하나가 바닥에 굴러다니는
  // 것처럼 보여 `__dbg.pick` 으로 재 보니 `mfp_printer` 의 박스 최저점이 y=0 이었다 —
  // 받침을 안 깔고 놓은 내 배치 실수다. 책상을 깔고 상판(y=0.76) 위에 올린다.
  // 창(z=10 벽, x≈30.9)보다 왼쪽이라 상판 0.76 이 창턱 0.95 를 가리지 않는다.
  // **난방은 `coal_stove_bare` 다.** 원래 `coal_stove` 는 재통 안의 탄 종이가 WS01 단서라
  // 단서 없는 방 두 곳에 태운 종이 더미를 놓는다 — 순수한 헛다리다. 그래서 재통을 뺀
  // 변형을 쓴다.
  // **난로를 GLB(`briquette_stove`)로 되돌리지 마라.** 2026-09-05 에 넣었더니 Tripo 재질의
  // 셰이더가 `VALIDATE_STATUS false` 로 링크에 실패했다. 다만 한때 이것을 WebGL 컨텍스트
  // 분실의 원인이라고 적어 뒀는데 **그건 틀렸다** — 분실의 진짜 원인은 크롬 GPU 워치독이고
  // (촬영 도구에 `--disable-gpu-watchdog` 로 잡았다. 기본 3/3 분실 → 플래그 0/3),
  // 레벨과는 무관하다. 여기 남는 사실은 두 개다: GLB 는 셰이더 링크에 실패하니 쓰지 말 것,
  // 그리고 단서 없는 방에는 재통 없는 변형을 쓸 것.
  // **난로는 등을 벽에 붙인다.** 조각은 화구 문이 -Z 를 보게 만들어져 있어서
  // 회전 없이 놓으면 등(+Z)이 방 한가운데를 향한다 — 연통이 갈 벽이 없다.
  // 여기서는 π 를 줘서 등을 z=0 벽에 붙이고 문이 방을 보게 한다.
  /* ================================================================== *
   * 조합센터 — 낮에 여섯이 드나들고 밤에는 빈 사무동. (모두 y = 0)
   *
   * **사람**: 배정호(재무, 흔적을 안 남기려는 사람 — 재무실만 정돈돼 있고
   * 서류함이 잠겨 있다) · 명주원(시설, 기록보관실 서류철) · 차유리(카페·로스터리,
   * 선판매 홍보) · 송이경(사진·기록, 회의실에 장비를 두고 다닌다) · 윤가람(도예).
   * 공용 공간이라 **개인 흔적이 섞인다** — 그것이 이 건물의 성격이다.
   *
   * 단서 자리를 먼저 찍고 가구를 그 주위에 세웠다(`interior-design` 스킬 1단계).
   * ================================================================== */

  /* -- 현관 cc_hall (−14..14 × −2..4, tile) -- */
  // E08 — 공용 비상키 캐비닛 **옆** 규칙표. 「비상키 위치는 전원 인지」가 이 단서다.
  // 규칙표가 캐비닛 옆에 붙어 있어야 「누구나 본다」가 읽힌다. `hangs: true`
  // E02 — 입주 기념사진 액자. 하단 날짜 2024.12.20 이 G01 힌트다. 현관 정면 벽

  /* -- 공용사무실 cc_office (−14..−4 × −16..−2) -- */
  // E05 — 사업일정 게시판. 「12/19 18:00 전환금 6.4억」이 여기 붙어 있다.
  // 게시판은 보여 주려고 만든 물건이라 가려지지 않는 것이 맞다

  /* -- 회의실 cc_meet (−4..6 × −16..−2) -- */
  // 가운데 섬은 회의탁자다. 좌석마다 사람이 남기고 간 것이 붙는다 — 그게 E18·E40 이다
  // E01 — 정면 화이트보드. 다섯 문제가 여기 적혀 있다. 방에 들어서면 정면
  // E06 — 벽걸이 공용캘린더. 12/19 20:30 최종회의가 16일에 등록돼 있다
  // E18 — 배정호 좌석 **옆 바닥**의 서류가방. 앉았던 자리 옆에 내려놓는 물건이다
  // E40 — 송이경 좌석 **등받이에 걸린** 카메라. 사진 담당이 회의 중 벗어 둔 것

  /* -- 재무실 bj_fin (6..14 × −16..−2) -- */
  // **배정호의 방이다.** 흔적을 안 남기려는 사람이라 정돈돼 있고 잠겨 있다.
  // 다른 방과 달리 바닥에 물건이 없다 — 그 「너무 깨끗함」이 이 사람의 흔적이다
  // E13 — 회계 PC 의 작업 스케줄러. 예약출력이 E12 를 만든다
  // E12 — 프린터 최근 작업기록. 23:40 전후 출력. 책상 옆 받침 위(바닥 금지)
  // E15·E16 — G02 잠금서류함. 상단 계약철 · 하단 담보봉투. **잠긴 물건은 눈에 띈다**
  // E17 — 공용태블릿 충전도크. R1~R2 에는 「압수」 표식만 남는다

  /* -- 카페·로스터리 cy_cafe (−14..−4 × −28..−16, concrete) -- */
  // E41 — 주문관리 카운터의 선판매 대시보드. 카운터가 이 방의 섬이다
  // E27 — 택배포장대 **아래** 교환·반품 박스. 윤가람 단서가 차유리 공간에 있다

  /* -- 공동주방 cc_kitchen (−4..6 × −28..−16, tile) --
   * 단서가 없는 방이다. **단서처럼 생긴 것을 두지 않는다** — 생활만 놓는다.
   *
   * 첫 배치는 흰 상자 둘과 가운데 식탁 하나뿐이라 주방으로 안 읽혔다(Chris).
   * 주방을 주방으로 만드는 것은 **작업 삼각형**이다 — 냉장고·싱크·조리대가
   * 서로 2~3m 안에서 ㄱ 자로 붙고, 그 사이를 사람이 두 걸음에 오간다.
   * 그리고 **상판 위에 물건이 있어야 한다.** 빈 상판은 전시장이다.
   * 여섯이 같이 쓰는 공동주방이라 그릇이 많고 정리가 반쯤 되어 있다.
   */
  // ── 작업 삼각형: 냉장고(서) → 싱크(중) → 조리대(동), 전부 북벽 z=−27.6 에 붙는다
  // **rot π.** 앞면이 −Z 규약이라 그냥 두면 북벽을 보고 선다 — 방에서는 뒤판
  // 방열 그릴 아홉 줄만 보였다(Chris 「이거 뭐냐 냉장고임?」). 의자 때와 같은 실수
  // 상판 위 — 싱크(0.86) · 조리대(0.9). 빈 상판은 없다
  // ── 상부 수납. 싱크 위가 비면 벽이 판이다. 창(z=−28 벽)을 피해 양옆으로
  // **장독대를 실내에 두지 마라.** 마당 조각이라 항아리 뚜껑에 눈이 얹혀 들어온다
  // (K2 컷에서 실내 주방에 눈 쌓인 독 다섯이 서 있었다). 대신 실내용 수납을 둔다.
  // 선반을 채운다 — 단 높이 0.129/0.709/1.289/1.869
  /**
   * ── 섬: 식탁 **둘을 잇대어** 놓는다.
   *
   * `dining_table` 은 1.6×0.92 짜리 4인용이다. 조합원이 여섯인데 여기에 의자
   * 다섯을 억지로 붙여 놓으니 식탁이 작아 보였다(2026-09-06 Chris: 「식탁 크기도
   * 왜 작은건지 아니면 수가 적은건지」). 공동주방은 식탁을 **잇대어** 긴 상을
   * 만든다 — 1.6 두 장이면 3.24m, 긴 변에 셋씩 여섯이 앉는다.
   *
   * **의자 방향도 전부 틀렸다.** `rot 0` 이 −Z 를 보는 규약인데 식탁이 어느 쪽에
   * 있는지를 안 보고 π 만 붙였다. 긴 변 북쪽(z=−22.95)에 앉는 사람은 +Z 를 보므로
   * π, 남쪽(z=−21.05)은 −Z 라 0 이다.
   */
  // 북쪽 줄 — 식탁이 +Z 에 있으므로 π
  // 남쪽 줄 — 식탁이 −Z 에 있으므로 0
  // **한 자리는 빠져 있다.** 방금 일어난 사람 — 의자가 밀려 나오고 살짝 돌아 있다

  // ── 난방. 여섯이 겨울에 모이는 방이다. 등을 서벽에
  // 뒷문(0,−28) 앞은 **양쪽 1.6m 를 비운다.** 조리 구간을 문 서쪽으로 몰았다 —
  // 싱크와 조리대가 문을 사이에 두고 갈라지면 조리 동선이 문을 가로지른다
  // ── 시선 띠 · 바닥 층

  /* -- 기록보관실 cc_arch (6..14 × −28..−16) -- */
  // **명주원의 서류철이 사는 방.** 선반이 섬이 아니라 벽선을 다 채운다 —
  // 보관실은 가운데가 비고 벽이 꽉 차야 보관실로 읽힌다
  // E19 시설관리철 B-2 · E22 2025 시설완료보고 · E34 사진성과보고철 A-4 · E42 2단계 승인철 C-1
  // **선반은 채운다** — 바인더 넷으로는 빈 선반이었다(컷 확인). 단 높이 0.129/0.709/1.289/1.869

  /* -- 조합센터 장식 — 시선 띠·바닥 층 (`decor-catalog.md` 「방 종류별 최소 구성」) --
   * 첫 배치가 단서·섬·벽선까지만이라 긴 벽이 빈 판이었다. 방마다 시계 하나,
   * 공용 건물이라 문 옆 소화기, 구석 화분 하나, 책상마다 쓰레기통.
   * 달력은 **단서 아닌 방에도** 건다 — 회의실 달력(E06)만 있으면 그게 단서다. */
  // 현관
  // 공용사무실
  // 회의실 — 긴 벽 둘(x=−4, x=6)이 비어 있었다
  // 재무실 — 깨끗한 사람. 시계·달력만. 바닥엔 쓰레기통 하나
  // 카페
  // 기록보관실

  /* ================================================================== *
   * 한서진의 집 — 사건 현장. (`hs_room` x −8..6 · z −66..−52 · y = HILL_Y 3.6)
   *
   * **단서 자리를 먼저 찍고 가구를 그 주위에 세웠다.** 반대로 하면 가구가
   * 예뻐지고 단서가 갈 곳을 잃는다. `scenario/사흘/05_단서표.md` 가 자리를
   * 이미 정해 놨다:
   *   E03 침실 > 업무테이블 왼쪽(휴대폰)   E07 침실 > 벽면 온도조절기
   *   E09 업무공간 > 노트북                E10 침실 > 스마트스피커
   *   E11 침실 > 붙박이장 안 시신           E21 거실 > 업무서랍 파일철
   *   E28 거실과 침실 사이 바닥 > 깨진 머그  E30 업무공간 > 인쇄 메모
   *
   * 현관은 z=−52 벽(`entrances` x=−1)이다. **네 칸으로 벽을 세운다** (Chris 09-07) —
   * 거실(−8..1 × −58..−52)·업무공간(1..6 × −60..−52)·침실(−8..1 × −66..−58)·다용도실.
   * 예전엔 「벽을 세우면 시야가 끊긴다」고 한 방에 넣었는데, 집이 집으로 안 읽혔다.
   * 침실 문은 닫혀 있다 — 사흘 동안 아무도 안 연 방이다.
   *
   * **방향 규약**: 조각은 −Z 를 본다. rot 0 = −Z, π = +Z, π/2 = −X, −π/2 = +X.
   * 벽에 붙이는 것은 그 벽의 반대편을 보게 준다.
   * ================================================================== */

  /**
   * **방의 y 는 자동으로 안 더해진다.** `world.ts:675` 가 `p.pos` 를 그대로
   * `group.position.set` 한다. 한서진 집은 언덕 위(`hs_room.y = HILL_Y 3.6`)라
   * y 를 직접 적어야 한다 — 0 으로 적었더니 가구가 전부 언덕 **속**에 묻혀
   * 방이 텅 비어 나왔다(2026-09-06). 그래서 아래 y 는 전부 3.6 + 실제 높이다.
   */
  /* -- 거실 hs_room (−8..1 × −58..−52). 현관문 x=−1 -- */
  // E21 — 거실 업무서랍. 남벽에 붙여 북(+Z)을 본다 → rot π. 서랍 프롭 `hs_chest_drawer` 가 이 앞판이다

  /* -- 업무공간 hs_work (1..6 × −60..−52). 문 (1, −55) -- */
  // E09 — 노트북. 동벽 책상, 서(−X)를 본다 → rot π/2
  // E30·E33 — 인쇄물은 상판 위. 책상 길이 안(z −57.1..−55.7)에 둔다

  /* -- 침실 hs_bed (−8..1 × −66..−58). 문 (−3.5, −58) 닫힘 + 「마감 중」 -- */
  // E10 — 협탁 + 스마트스피커. 침대 서쪽
  // E11 — 붙박이장. **시신이 이 안에 있다.** 서벽에 붙여 +X 를 본다
  // 폭 2.0 — 누운 시신(1.7m)이 붙박이장 안에 들어가야 한다. 시신은 조각이 아니라 `body` 프롭
  // (배역 모델 han.glb + 누운 클립, props.ts buildBody). Chris 09-07: 「한서진 사체 모델 있지 않아?」
  // E03 — 침실 업무테이블. 북벽에 붙여 남(+Z)을 본다 → rot π. 단서는 「왼쪽」 휴대폰
  // E07 — 온도조절기. 북벽 안쪽면
  // HS11 — 붙박이장 앞 겨울옷 더미가 평소와 다르게 치워져 있다
  // E28 — 깨진 머그. 「거실과 침실 사이 바닥」 = 침실 문 바로 안쪽

  /* -- 다용도실 hs_util (1..6 × −66..−60). 문 (1,−63)·(3.5,−60). 배경 — 카드 없음 -- */

  // 신문실은 등(+Z)이 z=10 벽을 보므로 회전 없이 벽 쪽으로 당기기만 한다
];

/** 조사 대상. **아직 비어 있다** — 이 단계는 마을 모양만 본다 */
/**
 * **B0 — 첫 단서 하나.** 한서진 집 붙박이장 안의 시신. E 조사 → P 촬영 → 수첩
 * 폴라로이드에 붉은 돋보기 도장(감정 대상) → 칠판에 오르면 부검이 온다.
 * 이 한 줄이 전체 흐름의 시험대다. 나머지 카드는 건물 워크스루(B1~)에서 놓는다.
 */
export const PROPS: PropDef[] = [
  /* 관계 DSL 로 푼 조사물 (level-saholv7.place.ts PLACE_PROPS) */
  ...PLACED_PROPS,
  /* 목공동 난로 재받이(E20)는 관계 배치(level-saholv7.place.ts PLACE_PROPS, near 'mj_stove')다 —
   * 난로를 손으로 또 놓았다가 창·문·솔버 가구와 세 번 부딪혔다(2026-09-09). 난로는 이미 솔버가 놓는다. */
  /* ── 한서진 집 거실 배경 조사물 둘 — 열어도 정보 없음(§0 규칙 4). 조각은 PIECES 의 책장·난로 ── */
  { id: 'hs_shelf', kind: 'exam', name: '거실 책장', room: 'hs_room', pos: [0.2, 0.9, -57.3], rot: Math.PI, clue: 'c_hs_shelf', reach: 1.8, look: 'marker' },
  { id: 'hs_stove', kind: 'exam', name: '거실 연탄난로', room: 'hs_room', pos: [-1.3, 0.4, -56.8], rot: Math.PI, clue: 'c_hs_stove', reach: 1.8, look: 'marker' },
  /* 암실 안전등 스위치 — **기본은 꺼짐.** 암실은 어두운 게 기본이고, 켜야 붉어진다
   * (2026-09-10, Chris: 「기본으로 어둡고 불이 켜지면 붉은색」). 문 안쪽 벽 */
  { id: 'si_dark_sw', kind: 'switch', name: '암실 안전등 스위치', room: 'si_dark', pos: [12.62, 1.25, 39.9], rot: Math.PI / 2, lightRoom: 'si_dark', init: { on: false }, reach: 1.6 },
  /* 암실 문 — 사진실에서 들어간다. 인화 중엔 닫아 둔다 */
  { id: 'si_door_dark', kind: 'door', name: '암실 문', room: 'si_photo', pos: [12.4, 1.1, 39.4], rot: Math.PI / 2, init: { open: false, locked: false } },
  /* ── 주택 다섯 칸막이 문 (2026-09-10). x 축 벽 rot 0 · z 축 벽 rot π/2. 처음엔 닫힘 ── */
  { id: 'si_door_bed', kind: 'door', name: '송이경 집 침실 문', room: 'si_room', pos: [-40, 1.1, -10.5], rot: 0, init: { open: false, locked: false } },
  { id: 'si_door_util', kind: 'door', name: '송이경 집 다용도실 문', room: 'si_kit', pos: [-34, 1.1, -10.5], rot: 0, init: { open: false, locked: false } },
  { id: 'si_door_kit', kind: 'door', name: '송이경 집 주방 문', room: 'si_room', pos: [-36, 1.1, -8], rot: Math.PI / 2, init: { open: false, locked: false } },
  { id: 'si_door_bu', kind: 'door', name: '송이경 집 침실-다용도 문', room: 'si_bed', pos: [-37, 1.1, -14], rot: Math.PI / 2, init: { open: false, locked: false } },
  { id: 'yg_door_bed', kind: 'door', name: '윤가람 집 침실 문', room: 'yg_room', pos: [40, 1.1, -10.5], rot: 0, init: { open: false, locked: false } },
  { id: 'yg_door_util', kind: 'door', name: '윤가람 집 다용도실 문', room: 'yg_kit', pos: [34, 1.1, -10.5], rot: 0, init: { open: false, locked: false } },
  { id: 'yg_door_kit', kind: 'door', name: '윤가람 집 주방 문', room: 'yg_room', pos: [36, 1.1, -8], rot: Math.PI / 2, init: { open: false, locked: false } },
  { id: 'yg_door_bu', kind: 'door', name: '윤가람 집 침실-다용도 문', room: 'yg_bed', pos: [37, 1.1, -14], rot: Math.PI / 2, init: { open: false, locked: false } },
  { id: 'mj_door_bed', kind: 'door', name: '명주원 집 침실 문', room: 'mj_room', pos: [-55, 1.1, 23.5], rot: 0, init: { open: false, locked: false } },
  { id: 'mj_door_util', kind: 'door', name: '명주원 집 다용도실 문', room: 'mj_kit', pos: [-47.5, 1.1, 23.5], rot: 0, init: { open: false, locked: false } },
  { id: 'mj_door_kit', kind: 'door', name: '명주원 집 주방 문', room: 'mj_room', pos: [-49, 1.1, 20], rot: Math.PI / 2, init: { open: false, locked: false } },
  { id: 'mj_door_bu', kind: 'door', name: '명주원 집 침실-다용도 문', room: 'mj_bed', pos: [-52, 1.1, 27], rot: Math.PI / 2, init: { open: false, locked: false } },
  // 화장실·창고 문 (2026-09-15)
  { id: 'mj_door_bath', kind: 'door', name: '명주원 집 화장실 문', room: 'mj_util', pos: [-51, 1.1, 27.7], rot: 0, init: { open: false, locked: false } },
  { id: 'mj_door_store', kind: 'door', name: '명주원 집 창고 문', room: 'mj_util', pos: [-48, 1.1, 27.7], rot: 0, init: { open: false, locked: false } },
  { id: 'cy_door_bath', kind: 'door', name: '차유리 집 화장실 문', room: 'cy_util', pos: [57, 1.1, 27.7], rot: 0, init: { open: false, locked: false } },
  { id: 'cy_door_store', kind: 'door', name: '차유리 집 창고 문', room: 'cy_util', pos: [54, 1.1, 27.7], rot: 0, init: { open: false, locked: false } },
  { id: 'si_door_bath', kind: 'door', name: '송이경 집 화장실 문', room: 'si_util', pos: [-36, 1.1, -15], rot: 0, init: { open: false, locked: false } },
  { id: 'si_door_store', kind: 'door', name: '송이경 집 창고 문', room: 'si_util', pos: [-33.5, 1.1, -15], rot: 0, init: { open: false, locked: false } },
  { id: 'yg_door_bath', kind: 'door', name: '윤가람 집 화장실 문', room: 'yg_util', pos: [33, 1.1, -15], rot: 0, init: { open: false, locked: false } },
  { id: 'yg_door_store', kind: 'door', name: '윤가람 집 창고 문', room: 'yg_util', pos: [35.5, 1.1, -15], rot: 0, init: { open: false, locked: false } },
  { id: 'hs_door_bath', kind: 'door', name: '한서진 집 화장실 문', room: 'hs_util', pos: [2, 1.1, -63.5], rot: 0, init: { open: false, locked: false } },
  { id: 'hs_door_store', kind: 'door', name: '한서진 집 창고 문', room: 'hs_util', pos: [4.5, 1.1, -63.5], rot: 0, init: { open: false, locked: false } },
  { id: 'bj_door_bath', kind: 'door', name: '배정호 집 화장실 문', room: 'bj_util', pos: [98, 1.1, -1], rot: Math.PI / 2, init: { open: false, locked: false } },
  { id: 'bj_door_store', kind: 'door', name: '배정호 집 창고 문', room: 'bj_util', pos: [98, 1.1, 2.5], rot: Math.PI / 2, init: { open: false, locked: false } },
  { id: 'cy_door_bed', kind: 'door', name: '차유리 집 침실 문', room: 'cy_room', pos: [49, 1.1, 23.5], rot: 0, init: { open: false, locked: false } },
  { id: 'cy_door_util', kind: 'door', name: '차유리 집 다용도실 문', room: 'cy_kit', pos: [56.5, 1.1, 23.5], rot: 0, init: { open: false, locked: false } },
  { id: 'cy_door_kit', kind: 'door', name: '차유리 집 주방 문', room: 'cy_room', pos: [55, 1.1, 20], rot: Math.PI / 2, init: { open: false, locked: false } },
  { id: 'cy_door_bu', kind: 'door', name: '차유리 집 침실-다용도 문', room: 'cy_bed', pos: [52, 1.1, 27], rot: Math.PI / 2, init: { open: false, locked: false } },
  { id: 'bj_door_bed', kind: 'door', name: '배정호 집 침실 문', room: 'bj_room', pos: [93, 1.1, -5], rot: Math.PI / 2, init: { open: false, locked: false } },
  { id: 'bj_door_util', kind: 'door', name: '배정호 집 다용도실 문', room: 'bj_kit', pos: [93, 1.1, 2.5], rot: Math.PI / 2, init: { open: false, locked: false } },
  { id: 'bj_door_kit', kind: 'door', name: '배정호 집 주방 문', room: 'bj_room', pos: [90.5, 1.1, 1], rot: 0, init: { open: false, locked: false } },
  { id: 'bj_door_bu', kind: 'door', name: '배정호 집 침실-다용도 문', room: 'bj_bed', pos: [96.5, 1.1, -2], rot: 0, init: { open: false, locked: false } },
  /* ── 한서진 집 — 문 다섯. 현관만 열려 있고 나머지는 닫혀 있다 ── */
  { id: 'hs_door_front', kind: 'door', name: '한서진 집 현관문', room: 'hs_room', pos: [-1, 1.1, -52], rot: 0, init: { open: false, locked: false } },
  { id: 'hs_door_bed', kind: 'door', name: '침실 문', room: 'hs_room', pos: [-3.5, 1.1, -58], rot: 0, init: { open: false, locked: false } },
  { id: 'hs_door_work', kind: 'door', name: '업무공간 문', room: 'hs_room', pos: [1, 1.1, -55], rot: Math.PI / 2, init: { open: false, locked: false } },
  { id: 'hs_door_util', kind: 'door', name: '다용도실 문', room: 'hs_bed', pos: [1, 1.1, -63], rot: Math.PI / 2, init: { open: false, locked: false } },
  { id: 'hs_door_util2', kind: 'door', name: '다용도실 문', room: 'hs_work', pos: [3.5, 1.1, -60], rot: 0, init: { open: false, locked: false } },

  /* ── 한서진 집 — 조사물 (HS01~HS20 중 이 집에 놓는 것). 카드 11 + 배경 6 ── */
  // 거실
  // 업무공간
  // 침실
  // id 는 반드시 `body` — props.ts 가 이 id 로 배역 모델을 누인다. rot 0 = 머리가 −Z(북쪽)

  /* ── 파기 자리 둘 (2026-09-22, 사흘v8 A9 — 예전엔 눈밭 여섯을 깔아 「삽 들면
   * 다 파 보게」 만들었는데, 그러면 진짜 자리가 노가다로 뚫렸다. 이제 파기는
   * 두 곳뿐이다). dig_ground 조각은 **아무것도 그리지 않는다** — 닫혀도 열려도
   * 눈밭 그대로다. **삽(shovel)을 들고 있지 않으면 조준해도 프롬프트가 아예
   * 뜨지 않는다** — 뜨는 순간 「여기 판다」가 들통나기 때문이다.
   * 진짜는 언덕 초입 **꺼진 가로등(L-07) 북쪽** — 범인이 스스로 꺼 뜨린 등
   * 아래, 눈이 금방 덮일 거라 믿고 피 묻은 변경계약을 묻은 자리다.
   * 다른 하나는 교문 옆 타임캡슐 — 팻말이 「여기 판다」를 미리 말해 주는
   * 훼이크다. 파 보면 서류가 아니라 2년 전 편지 여섯 장이 나온다. ── */
  { id: 'dig_lamp', kind: 'lid', name: '눈밭', room: 'outside', pos: [9.4, 0, -31.3], rot: 0.9, piece: 'dig_ground_packed', dig: true, needs: 'shovel', init: { open: false }, reach: 1.8 },
  /* 파낸 자리 밑의 진짜 — 시신 오른손 아래 조각(c_hs_hand)과 이어지는 종이 조각.
   * 누가 한 번 파 두면(hiddenIn 해제) 그 자리를 조준하는 사람은 누구든 조사할 수
   * 있다 — 땅은 겉으론 그대로라 아는 사람만 찾는다. needs 는 달지 않는다 */
  { id: 'dig_scrap', kind: 'exam', name: '흙 밑에 묻힌 것', room: 'outside', pos: [9.4, 0.04, -31.3], clue: 'c_dig_scrap', reach: 1.5, look: 'marker', hiddenIn: 'dig_lamp' },
  /* 교문 옆 일곱 번째 눈밭 + 팻말 + 캡슐 — 같은 dig 규칙이라 삽을 든 사람만 판다 */
  { id: 'dig_capsule', kind: 'lid', name: '눈밭', room: 'outside', pos: [6.4, 0, 14.0], rot: -0.5, piece: 'dig_ground', dig: true, needs: 'shovel', init: { open: false }, reach: 1.8 },
  { id: 'capsule_box', kind: 'exam', name: '묻혀 있던 통', room: 'outside', pos: [6.4, 0.04, 14.0], clue: 'c_v16f', reach: 1.5, look: 'marker', hiddenIn: 'dig_capsule' },
  { id: 'capsule_sign', kind: 'exam', name: '팻말과 표석', room: 'outside', pos: [5.9, 0, 13.6], rot: 0.2, clue: 'c_v16s', reach: 1.8, piece: 'capsule_marker' },
  /* 눈 위 발자국 두 벌 — 언덕길(RAMPS.hill) 비탈면 위에 붙는다 (A1). ①굵은 러그
   * 내리막 한 벌(위로 갈수록 눈에 메워짐) + ②작은 발 오르내림 두 벌(또렷).
   * rot π/2 → 룩의 로컬 +X(내리막)가 남쪽 마을을 본다. y 는 z=-37 자리의 경사면 높이 */
  { id: 'out_tracks', kind: 'exam', name: '언덕길 눈 위의 발자국', room: 'outside', pos: [0.3, 1.4, -37], rot: Math.PI / 2, clue: 'c_v17', reach: 2.4, look: 'tracks_pair' },
  { id: 'out_lamp', kind: 'exam', name: '꺼진 가로등', room: 'outside', pos: [9, 0.7, -29], clue: 'c_v19', reach: 2.2, look: 'marker' },

  /* ── 제설용 삽 — 조합센터 현관(cc_hall)에 세워 둔 것.
   * 이걸 들고 있어야 여섯 파기 자리의 「파기」가 활성화된다(needs). ── */
  { id: 'shovel', kind: 'pickup', name: '제설용 삽', room: 'cc_hall', pos: [1.6, 0, 3.4], rot: 0.6, reach: 1.6 },
];

/** v7 잠금. G01 — 노트북 PIN. 힌트는 조합센터 현관 액자(E02)의 날짜 2024.12.20 */
export const LOCKS: Record<string, LockDef> = {
  studio_cabinet: {
    kind: 'code', digits: 4,
    hint: '유약 캐비닛 다이얼 네 자리. 가마실 소성표의 최고 온도가 적혀 있다.',
    denied: '다이얼이 헛돈다.',
  },
  cafe_tablet: {
    kind: 'code', digits: 4,
    // 힌트는 **잠긴 물건 곁이 아니라 다른 물건**에 있다 — 옆에 붙여 두면 수수께끼가 아니라 안내문이다
    hint: '태블릿 잠금 네 자리. 로스터 옆 로스팅 프로파일에 형광펜이 그어진 줄이 있다.',
    denied: '비밀번호가 틀렸다고 뜬다.',
  },
  photo_ssd: {
    kind: 'code', digits: 4,
    hint: '외장 SSD 비밀번호 네 자리. 전시 포스터 구석에 숫자가 있다.',
    denied: '비밀번호가 틀렸다고 뜬다.',
  },
  fin_cab_top: {
    kind: 'code', digits: 4,
    hint: '다이얼 자물쇠 네 자리. 옆에 붙은 메모 — 「전환금 들어오는 날」.',
    denied: '다이얼이 헛돈다.',
  },
  photo_lab: {
    kind: 'code', digits: 6,
    hint: '「구간 이동」 — 타임코드 여섯 자리(시:분:초). 어느 구간을 잘랐는지는 자른 기기가 안다.',
    denied: '그 구간에는 작업 소음뿐이다.',
  },
  hs_laptop: {
    kind: 'code', digits: 6,
    hint: '잠금화면에서 멈춰 있다. PIN 여섯 자리. 힌트란에 「730일이 시작된 날」.',
    denied: 'PIN이 틀렸다고 뜬다.',
  },
  hs_doorlock: {
    kind: 'code', digits: 4,
    // 자동잠금형 — 정면에는 아무 기록도 없다. 관리자 이력은 관리자 코드로만 열린다.
    // 코드는 조합센터 열쇠함 안 「비상용 관리자 봉투」(c_v05)에 봉인돼 있다
    hint: '관리자 코드 네 자리. 조합센터 현관 열쇠함에 봉인된 봉투가 있다.',
    denied: '삑— 틀린 코드다. 이력 화면은 열리지 않는다.',
  },
};
export const PROP_BY_ID: Record<string, PropDef> = Object.fromEntries(PROPS.map((p) => [p.id, p]));

export function initialObjects(): Record<string, ObjState> {
  const out: Record<string, ObjState> = {};
  for (const p of PROPS) out[p.id] = p.init ? { ...p.init } : p.kind === 'pickup' ? { taken: false } : {};
  return out;
}

/**
 * 시신 부위 — 시신을 클릭하면 뜨는 조사 화면. 단서가 있는 부위 둘(목·오른손), 나머지는 상태 설명.
 * `under` 는 「옷 걷기」를 눌러야 보인다. 본문은 secrets-saholv7 V7_BODY(단서 없는 부위) / CLUES(단서 부위)
 */
export const BODY_PARTS: BodyPartDef[] = [
  { id: 'head', label: '머리' }, { id: 'face', label: '얼굴' },
  { id: 'neck', label: '목·옷깃', clue: 'c_hs_neck' },
  { id: 'chest', label: '가슴·상의' }, { id: 'chest_skin', label: '상의 안쪽', under: true },
  { id: 'pocket_top', label: '상의 주머니', under: true },
  { id: 'belly', label: '배' }, { id: 'back', label: '등', under: true },
  { id: 'arm_l', label: '왼팔' }, { id: 'arm_r', label: '오른팔' },
  { id: 'hand_l', label: '왼손', clue: 'c_hs_hand_l' }, { id: 'hand_r', label: '오른손', clue: 'c_hs_hand' },
  { id: 'pocket_pants', label: '바지 주머니' },
  { id: 'legs', label: '다리' }, { id: 'feet', label: '발' },
];

/** 감정서 — 시신이 칠판에 오르면 2차 시작에 부검이 탐정에게 오고 그 자리에서 공개된다. 3차엔 조건 없이 온다 */
export const FORENSICS: ForensicDef[] = [
  { id: 'c_e04', needsAny: ['c_body', 'c_hs_neck', 'c_hs_hand'], round: 2, auto: true, backupRound: 3, labMinutes: 5 },
  /* F02 교살 흔 — 목의 흔적이 올라오면, 흉기 후보(어깨끈·케이블·가방끈·녹슨 철사) 중
   * 하나라도 발견돼 있을 때 비로소 「무엇으로」가 감정된다 */
  { id: 'c_f02', needsAll: ['c_hs_neck'], needsAny: ['c_e18', 'c_pr10', 'c_e25', 'c_e40'], round: 3, labMinutes: 5 },
  /* F03 발자국 감정 — 언덕 초입 발자국이 발견되면 밑창 패턴·보폭·방향을 감정한다.
   * 묻힌 ①번은 강설 중에만 생기는 자국 — 범인의 하산 발자국이며 270~280 세 켤레
   * (명·배·차)로 좁아진다. 송이경 소거의 두 번째 문 — V38 사슬이 안 열려도 온다 */
  { id: 'c_f03', needsAll: ['c_v17'], needsAny: [], round: 3, labMinutes: 4 },
  /* F04 감압지 접합 — 오른손 조각(V12)과 매장 서류(V16)가 둘 다 칠판에 올라야
   * 「같은 종이」가 성립한다 */
  { id: 'c_f04', needsAll: ['c_e11', 'c_dig_scrap'], needsAny: [], round: 3, labMinutes: 5 },
  /* F05 사후경직·각운동 — 시신만 올라오면 1차에 바로 온다. 「죽은 지 사흘」의 뿌리 */
  { id: 'c_f05', needsAny: ['c_body'], round: 1, auto: true, labMinutes: 3 },
  /* ── 진술 카드 (V38·V43) — 「해금 뒤 신문실 서랍에 놓인다」(05·10_오브젝트).
   * deliverTo 로 칠판이 아니라 서랍을 채운다; 서랍을 여는 사람이 카드를 핀다.
   * 진술은 실험실 일이 아니라서 labMinutes 없이 라운드 경계에 바로 온다 ── */
  /* V38 윤가람 — 「윤가람 방문이 드러나야 말한다」: V13 머그·V36 파편통·V37 휴대폰 중 둘 */
  { id: 'c_v38', needsAny: [], needsAll: ['c_e28', 'c_e29'], round: 2, auto: true, deliverTo: 'intv_memo_yg', label: '진술' },
  { id: 'c_v38', needsAny: [], needsAll: ['c_e28', 'c_v37'], round: 2, auto: true, deliverTo: 'intv_memo_yg', label: '진술' },
  { id: 'c_v38', needsAny: [], needsAll: ['c_e29', 'c_v37'], round: 2, auto: true, deliverTo: 'intv_memo_yg', label: '진술' },
  /* V43 송이경 — 노트북 USB 연결 기록(c_e36)이 오르거나, 봉투+발자국(c_v05·c_v17)이 함께 오르면 */
  { id: 'c_v43', needsAny: ['c_e36'], round: 2, auto: true, deliverTo: 'intv_memo_si', label: '진술' },
  { id: 'c_v43', needsAny: [], needsAll: ['c_v05', 'c_v17'], round: 2, auto: true, deliverTo: 'intv_memo_si', label: '진술' },
];

export type { LinkDef, LockDef, LockKind, RoundDef, RoundMode } from './level-defs';

/* ── ROUNDS ──
 * 사흘 v8 7단계: 예산 R1 12/필름3 · R2 12/3 · R3 4/1, pinLimit (6,6,2).
 * 시뮬(scenario/사흘/93_시뮬레이션_v8.md): 1·2차 6장 + 최종 1장 → 검거율 ~52%.
 * F03 캐스트가 송이경을 직접 소거하는 수정과 한 세트 — 그 수정 없이 (3,3,1) 은 0%. */
const ALL_ZONES = ROOMS.map((r) => r.id).concat('outside');

export const ROUNDS: RoundDef[] = [
  { id: 1, name: '1차 조사', zones: ALL_ZONES, budget: 12, film: 3, minutes: 12, assign: true, mode: 'squad', perTurn: 4, pinLimit: 6 },
  { id: 2, name: '2차 조사', zones: ALL_ZONES, budget: 12, film: 3, minutes: 10, assign: false, mode: 'free', perTurn: 10, pinLimit: 6 },
  { id: 3, name: '마지막 조사', zones: ALL_ZONES, budget: 4, film: 1, minutes: 5, assign: false, mode: 'solo', perTurn: 2, pinLimit: 2 },
];

export function roundDef(n: number): RoundDef {
  return ROUNDS[Math.max(0, Math.min(ROUNDS.length - 1, n - 1))];
}

/* ── 조사 규칙 ──
 * **`SANDBOX` 가 true 면 조 편성·라운드 잠금(`fromRound`)·조사 횟수가 전부 열린다.**
 *
 * 2026-09-23 정리 때 확인한 사실: 「7단계 — 켜기」(3ea3d35)가 이 파일에 `SANDBOX = false` 를
 * 적었지만, 판정 함수(investigateBlock·squadBlock)를 v3 레벨 파일에서 빌려 오고 있어서
 * **v3 의 `SANDBOX = true` 가 실제로 돌았다** — 라이브는 규칙이 꺼진 채였다.
 * 정리는 라이브 동작을 그대로 옮겼다(true). false 로 바꾸면 위 ROUNDS 대로 규칙이 돈다
 * (`shared/rules.ts` roundGuards) — 켜기 전에 1차 조 편성(O/F8) 흐름을 실제 판에서 확인할 것. */
export const SANDBOX = true;
const guards = roundGuards(ROUNDS);

export function investigateBlock(
  round: number,
  prop: { room: string; fromRound?: number; lateReason?: string },
  assignedZone: string | undefined,
  budgetLeft: number | undefined,
): string | null {
  return SANDBOX ? null : guards.investigateBlock(round, prop, assignedZone, budgetLeft);
}

export function squadBlock(round: number, out: string[], done: string[], playerId: string): string | null {
  return SANDBOX ? null : guards.squadBlock(round, out, done, playerId);
}

export const readyPair = readyPairOf;

/** 프롭이 속한 구역 = 방 */
export function zoneOf(p: PropDef): string {
  return p.room;
}

/** 문은 라운드로 막지 않는다. 잠긴 문은 열쇠로 잠겨 있을 뿐이다 */
export function zoneOpen(_round: number, _room: string): boolean {
  return true;
}

/** 단서 연결(추론 카드 자동 공개). 이 판에는 없다 */
export const LINKS: LinkDef[] = [];

export function newLinks(found: Record<string, unknown>): LinkDef[] {
  return LINKS.filter((l) => !found[l.id] && l.needs.every((n) => found[n]));
}

/** 에피소드 공통 이름 — 배럴·에디터는 이 이름으로 읽는다 */
export { SAHOLV7_LEVEL as LEVEL } from './level-saholv7';
